/**
 * @fileoverview Foreground FCM message subscription hook.
 * @description Subscribes to Firebase Cloud Messaging's onMessage event, which only fires while
 * the app's tab is open and focused — background/tab-closed delivery is handled separately by
 * public/firebase-messaging-sw.js. firebase/messaging is loaded via a dynamic import, rather than
 * a static one, so its code never enters the main bundle for the common case (no Firebase project
 * configured) — it only loads once this hook actually has a Firebase app to subscribe against.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect } from 'react'

import { getFirebaseApp } from '@/config/firebase'

import type { MessagePayload, Unsubscribe } from 'firebase/messaging'

/**
 * @description Invokes onForegroundMessage for every FCM message received while the app is open
 * and focused.
 * @param {(payload: MessagePayload) => void} onForegroundMessage - Called with each message
 * payload. Wrap in useCallback so the subscription isn't torn down and recreated every render.
 */
export function useForegroundMessages(
  onForegroundMessage: (payload: MessagePayload) => void,
): void {
  useEffect(() => {
    const app = getFirebaseApp()

    if (!app) {
      return
    }

    let cancelled = false
    let unsubscribe: Unsubscribe | undefined

    void import('firebase/messaging').then(({ getMessaging, onMessage }) => {
      if (cancelled) {
        return
      }

      unsubscribe = onMessage(getMessaging(app), onForegroundMessage)
    })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [onForegroundMessage])
}
